import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert, ProgressBar, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import '../App.css';

// The analysis credits feature, as the plans import names it.
const c_CreditsFeatureCode = 'MONTHLY_ANALYSIS_CREDITS';

const get_credits_bar_variant = (used, total) => {
	if(total === 0) {
		return 'secondary';
	}

	const pct = used / total;
	if(pct >= 0.8) {
		return 'danger';
	}

	if(pct >= 0.5) {
		return 'warning';
	}

	return 'success';
};

const render_credits = (quota, subscription_features) => {
	const features_list = subscription_features ? subscription_features : [];
	const credits_feature = features_list.find(function(feature) {
		return feature.code === c_CreditsFeatureCode;
	});

	const total = credits_feature ? parseInt(credits_feature.limit_value ? credits_feature.limit_value : '0', 10) : 0;
	if(total <= 0) {
		return null;
	}

	var remaining = total;
	if(quota) {
		remaining = (quota.remaining != null) ? quota.remaining : 0;
	}

	const used = Math.max(0, total - remaining);
	const pct_used = Math.min(100, (used / total) * 100);

	// Whole percentages hide small usage on a large plan (12 of 10,000 rounds to 0%), so below 10% one decimal
	// place is shown, and the count of credits used is shown alongside.
	var pct_text = '0';
	if(used > 0){
		pct_text = (pct_used < 10) ? pct_used.toFixed(1) : String(Math.round(pct_used));
	}

	const used_text = used.toLocaleString() + ' used this month (' + pct_text + '%)';

	return (
		<div className="profile-sub-credits">
			<div className="profile-sub-credits-header">
				<span className="profile-field-label">Analysis Credits</span>
				<span className="profile-sub-credits-count">
					{remaining.toLocaleString() + ' / ' + total.toLocaleString() + ' remaining'}
				</span>
			</div>
			<ProgressBar
				now={pct_used}
				variant={get_credits_bar_variant(used, total)}
				className="profile-sub-credits-bar"
			/>
			<span className="profile-sub-credits-pct">{used_text}</span>
		</div>
	);
};

const render_suspended = (subscription) => {
	return (
		<div className="profile-sub-suspended">
			<span className="profile-field-label profile-sub-suspended-title">
				{subscription.suspended_plan_name ?
					('Your ' + subscription.suspended_plan_name + ' plan is on hold.') :
					'Your plan is on hold.'}
			</span>
			<span className="profile-field-label">
				A payment did not go through. Your plan and its credits stay as they are and
				come back as soon as a payment succeeds.
			</span>
		</div>
	);
};

const render_no_plan = (navigate) => {
	return (
		<div className="profile-sub-no-plan">
			<span className="profile-field-label">No active subscription.</span>
			<Button
				variant="primary"
				size="sm"
				className="profile-sub-upgrade-btn"
				onClick={() => { navigate('/plans'); }}>
				View Plans
			</Button>
		</div>
	);
};

const render_subscription_section = (subscription, quota, subscription_features, navigate) => {
	const plan_name = subscription ? subscription.plan_name : null;

	if(plan_name) {
		return (
			<div className="profile-section">
				<h5 className="profile-section-title">Subscription</h5>
				<div className="profile-sub-plan-row">
					<span className="profile-field-label">Plan</span>
					<span className="profile-field-value profile-field-value-bold">{plan_name}</span>
				</div>
				{render_credits(quota, subscription_features)}
			</div>
		);
	}

	return (
		<div className="profile-section">
			<h5 className="profile-section-title">Subscription</h5>
			{(subscription && subscription.suspended) ? render_suspended(subscription) : render_no_plan(navigate)}
		</div>
	);
};

const render_email_section = (email) => {
	return (
		<div className="profile-section">
			<h5 className="profile-section-title">Email</h5>
			<div className="profile-field">
				<span className="profile-field-label">Email</span>
				<span className="profile-field-value">{email}</span>
			</div>
		</div>
	);
};

const render_password_section = (navigate, email) => {
	return (
		<div className="profile-section">
			<h5 className="profile-section-title">Password</h5>
			<Button
				variant="primary"
				onClick={() => { navigate('/request_password_reset', { state: { email } }); }}>
				Change Password
			</Button>
		</div>
	);
};

// One switch of the App Settings section, with its description. on_change is called with the switch's new value.
const render_setting_switch = (id, label, description, checked, saving, on_change) => {
	return (
		<React.Fragment>
			<div className="profile-setting-row">
				<Form.Check
					type="switch"
					id={id}
					className="profile-setting-switch"
					label={label}
					checked={checked}
					disabled={saving}
					onChange={(event) => { on_change(event.target.checked); }}
				/>
			</div>
			<p className="profile-field-label profile-setting-description">{description}</p>
		</React.Fragment>
	);
};

// The App Settings section, for every user: their own settings, and the historical data import, an app-wide
// setting, for a superadmin only (the backend only sends it, and only accepts changes to it, from a superadmin).
// on_change is called with the settings to change, by name.
const render_app_settings_section = (app_settings, saving_app_settings, app_settings_error, is_superadmin, on_change) => {
	if(!app_settings) {
		return (
			<div className="profile-section">
				<h5 className="profile-section-title">App Settings</h5>
				{app_settings_error ? <Alert variant="danger">{app_settings_error}</Alert> : <Spinner animation="border" size="sm" />}
			</div>
		);
	}

	const shows_history_import = is_superadmin && (typeof app_settings.history_import_enabled === 'boolean');

	return (
		<div className="profile-section">
			<div className="profile-setting-title-row">
				<h5 className="profile-section-title">App Settings</h5>
				{saving_app_settings && <Spinner animation="border" size="sm" />}
			</div>
			{render_setting_switch(
				'site-notes-collapsed',
				'Collapse Site Notes by default',
				'When on, the Site Notes section of a site analysis starts collapsed, showing only its first lines. You can still open it.',
				app_settings.site_notes_collapsed,
				saving_app_settings,
				(checked) => { on_change({ site_notes_collapsed: checked }); }
			)}
			{shows_history_import && render_setting_switch(
				'history-import-enabled',
				'Historical data import',
				'When on, each site crawl imports the site\'s history from the web archive (archive.org). When off, sites are crawled and analyzed without it, and their history is imported by a crawl after it is turned back on.',
				app_settings.history_import_enabled,
				saving_app_settings,
				(checked) => { on_change({ history_import_enabled: checked }); }
			)}
			{app_settings_error && <Alert variant="danger">{app_settings_error}</Alert>}
		</div>
	);
};

const render_danger_section = (on_delete_click) => {
	return (
		<div className="profile-section">
			<h5 className="profile-section-title profile-section-title-danger">Danger Zone</h5>
			<p className="profile-field-label">Permanently delete your account and all associated data.</p>
			<Button
				variant="danger"
				onClick={on_delete_click}>
				Delete Account
			</Button>
		</div>
	);
};

function ProfileView() {
	const navigate = useNavigate();
	const { token, is_superadmin } = useAuth();
	const email = localStorage.getItem(Constants.USER_EMAIL);
	const user_id = localStorage.getItem(Constants.USER_ID);

	const [show_confirm, set_show_confirm] = useState(false);
	const [deleting, set_deleting] = useState(false);
	const [error, set_error] = useState('');
	const [quota, set_quota] = useState(null);
	const [subscription, set_subscription] = useState(null);
	const [subscription_features, set_subscription_features] = useState([]);
	const [loading_subscription, set_loading_subscription] = useState(true);
	const [app_settings, set_app_settings] = useState(null);
	const [saving_app_settings, set_saving_app_settings] = useState(false);
	const [app_settings_error, set_app_settings_error] = useState('');

	// Loaded again when the superadmin check comes in, since a superadmin is also sent the app-wide settings.
	useEffect(function() {
		if(!token) {
			return;
		}

		const headers = { Authorization: 'Bearer ' + token };
		axios.get(Constants.SERVER_URL + '/api/app_settings', { headers }).then(function(response) {
			set_app_settings(response.data);
		}).catch(function(error) {
			console.log('ProfileView/load_app_settings - ERROR: ', describe_error(error));
			set_app_settings_error(get_user_error_message(error, 'The app settings could not be loaded.'));
		});
	}, [token, is_superadmin]);

	// Saves the settings in changes, by name, and shows the settings as stored.
	const handle_app_settings_change = async (changes) => {
		try {
			set_saving_app_settings(true);
			set_app_settings_error('');
			const headers = { Authorization: 'Bearer ' + token };
			const response = await axios.put(Constants.SERVER_URL + '/api/app_settings', changes, { headers });
			set_app_settings(response.data);
		} catch (error) {
			console.log('ProfileView/handle_app_settings_change - ERROR: ', describe_error(error));
			set_app_settings_error(get_user_error_message(error, 'The setting could not be saved. Please try again.'));
		}

		set_saving_app_settings(false);
	};

	useEffect(function() {
		if(!user_id || !token) {
			set_loading_subscription(false);
			return;
		}

		const headers = { Authorization: 'Bearer ' + token };

		Promise.allSettled([
			axios.get(Constants.SERVER_URL + '/api/quota', { headers }),
			axios.get(Constants.SERVER_URL + '/api/user_subscription', { headers }),
			axios.get(Constants.SERVER_URL + '/api/get_subscription_features', { headers }),
		]).then(function(results) {
			const quota_result = results[0];
			const sub_result = results[1];
			const features_result = results[2];

			if(quota_result.status === 'fulfilled') {
				set_quota(quota_result.value.data);
			}

			if(sub_result.status === 'fulfilled') {
				set_subscription(sub_result.value.data);
			}

			if(features_result.status === 'fulfilled' && features_result.value.data.features) {
				set_subscription_features(features_result.value.data.features);
			}

			set_loading_subscription(false);
		});
	}, [token, user_id]);

	const handle_delete = async () => {
		try {
			set_deleting(true);
			set_error('');
			const headers = { Authorization: 'Bearer ' + token };
			await axios.delete(Constants.SERVER_URL + '/api/user/' + user_id, { headers });
			localStorage.removeItem(Constants.TOKEN_NAME);
			localStorage.removeItem(Constants.USER_EMAIL);
			localStorage.removeItem(Constants.USER_ID);
			localStorage.removeItem(Constants.SIGNUP_SELECTED_PLAN);
			localStorage.removeItem(Constants.SIGNUP_USER_EMAIL);
			window.location.replace('/login');
		} catch (error) {
			console.log('handle_delete - ERROR: ', describe_error(error));
			set_error(get_user_error_message(error, 'Failed to delete account. Please try again.'));
			set_deleting(false);
		}
	};

	const render_subscription_or_loading = () => {
		if(loading_subscription) {
			return (
				<div className="profile-section">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		return render_subscription_section(subscription, quota, subscription_features, navigate);
	};

	const render_delete_modal = () => {
		return (
			<Modal show={show_confirm} onHide={() => { set_show_confirm(false); }} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete Account</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Are you sure you want to permanently delete your account? This cannot be undone.</p>
					{error && <Alert variant="danger">{error}</Alert>}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => { set_show_confirm(false); }} disabled={deleting}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_delete} disabled={deleting}>
						{deleting ? <Spinner size="sm" animation="border" /> : 'Delete My Account'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	return (
		<React.Fragment>
			<main className="view-container profile-main">
				<div className="profile-header">
					<h4 className="profile-title">Account</h4>
				</div>

				<div className="profile-card">
					{render_email_section(email)}
					{render_subscription_or_loading()}
					{render_password_section(navigate, email)}
					{render_app_settings_section(app_settings, saving_app_settings, app_settings_error, is_superadmin, handle_app_settings_change)}
					{render_danger_section(() => { set_show_confirm(true); })}
				</div>
			</main>

			{render_delete_modal()}
		</React.Fragment>
	);
}

export default ProfileView;
